import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { NetworkQualityBadge } from "./NetworkQualityBadge";
import { useIsMobile } from "@/hooks/use-mobile";
import { RefreshCw, Signal } from "lucide-react";

interface NetworkData {
  n?: string;
  p?: string;
  a?: number;
  ok?: number;
  s?: number;
  d?: number;
  rsrp?: number;
  rsrq?: number;
  snr?: number;
  rssi?: number;
  csq?: number;
}

interface AvailableNetworksListProps {
  activeProbe: NetworkData[];
  onSteerToNetwork: (plmn: string, operatorName: string, rat: number) => Promise<void>;
  onRevertToAuto: () => Promise<void>;
  onShowAutoDialog: () => void;
  onTriggerScan: () => Promise<void>;
  isUpdating: boolean;
  steerMode: string;
  cellScanNow?: number;
  currentNetworkProbe?: NetworkData[];
  steerPlmn?: string;
  activeProbeLastUpdate?: string;
  canEditDeviceConfig?: boolean;
}

export function AvailableNetworksList({
  activeProbe,
  onSteerToNetwork,
  onRevertToAuto,
  onShowAutoDialog,
  onTriggerScan,
  isUpdating,
  steerMode,
  cellScanNow,
  currentNetworkProbe,
  steerPlmn,
  activeProbeLastUpdate,
  canEditDeviceConfig = true
}: AvailableNetworksListProps) {
  const [filter, setFilter] = useState<'all' | 'lte' | '3g' | '2g'>('all');
  const isMobile = useIsMobile();

  // Check if scan is pending based on cellScanNow value (handle both number and string)
  const isScanPending = Number(cellScanNow) === 1;

  // Format the activeProbeLastUpdate timestamp
  const formatLastUpdate = (timestamp?: string) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      const timeString = date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const dateString = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      return ` (last updated: ${timeString} ${dateString})`;
    } catch (error) {
      console.error('Error formatting activeProbeLastUpdate:', error);
      return '';
    }
  };

  const getRATBadge = (rat?: number) => {
    switch (rat) {
      case 7: return <Badge variant="default" className="bg-green-400 hover:bg-green-500 text-white border-green-400 text-center justify-center">LTE (4G)</Badge>;
      case 2: return <Badge variant="secondary" className="bg-yellow-400 hover:bg-yellow-500 text-white border-yellow-400 text-center justify-center">UTRAN (3G)</Badge>;
      case 0: return <Badge variant="outline" className="bg-red-400 hover:bg-red-500 text-white border-red-400 text-center justify-center">GSM (2G)</Badge>;
      default: return <Badge variant="outline" className="text-center justify-center">Unknown</Badge>;
    }
  };

  const getAttachStatus = (ok?: number) => {
    return ok === 1 ? (
      <span className="text-green-600 font-medium">✓</span>
    ) : (
      <span className="text-red-600 font-medium">✗</span>
    );
  };

  // Debug logging
  console.log('AvailableNetworksList Debug:', {
    steerMode,
    isUpdating,
    showAutoButton: steerMode === 'MANUAL',
    onRevertToAuto: typeof onRevertToAuto
  });

  const calculateQualityScore = (network: NetworkData) => {
    let score = 0;
    if (network.rsrp) score += Math.max(0, Math.min(60, ((network.rsrp + 120) / 40) * 60));
    if (network.rsrq) score += Math.max(0, Math.min(25, ((network.rsrq + 15) / 8) * 25));
    if (network.snr) score += Math.max(0, Math.min(15, (network.snr / 20) * 15));
    else if (network.rssi) score += Math.max(0, Math.min(40, ((network.rssi + 110) / 40) * 40));
    else if (network.csq) score += Math.max(0, Math.min(40, ((network.csq - 5) / 20) * 40));
    return score;
  };

  const filterNetworks = (networks: NetworkData[]) => {
    // Ensure networks is always an array to prevent filter errors
    const safeNetworks = Array.isArray(networks) ? networks : [];
    
    return safeNetworks.filter(network => {
      // Hide forbidden networks (s=3)
      if (network.s === 3) return false;
      
      switch (filter) {
        case 'lte': return network.a === 7;
        case '3g': return network.a === 2;
        case '2g': return network.a === 0;
        default: return true;
      }
    });
  };

  const sortNetworks = (networks: NetworkData[]) => {
    // Ensure networks is always an array to prevent sort errors
    const safeNetworks = Array.isArray(networks) ? networks : [];
    
    return [...safeNetworks].sort((a, b) => {
      // Current network first
      if (a.s === 2 && b.s !== 2) return -1;
      if (b.s === 2 && a.s !== 2) return 1;
      
      // Then by RAT preference (LTE > 3G > 2G)
      const ratOrder = { 7: 3, 2: 2, 0: 1 };
      const aRat = ratOrder[a.a as keyof typeof ratOrder] || 0;
      const bRat = ratOrder[b.a as keyof typeof ratOrder] || 0;
      
      if (aRat !== bRat) return bRat - aRat;
      
      // Then by quality score (higher is better)
      return calculateQualityScore(b) - calculateQualityScore(a);
    });
  };

  // Ensure activeProbe is always an array
  const safeActiveProbe = Array.isArray(activeProbe) ? activeProbe : [];
  const filteredAndSortedNetworks = sortNetworks(filterNetworks(safeActiveProbe));

  const isCurrentlySelected = (network: NetworkData) => {
    // Check if this network matches the currently steered network
    if (steerMode === 'MANUAL' && steerPlmn && currentNetworkProbe && currentNetworkProbe.length > 0) {
      const currentNetwork = currentNetworkProbe[0];
      return network.p === steerPlmn && network.p === currentNetwork.p && network.a === currentNetwork.a;
    }
    return false;
  };

  if (safeActiveProbe.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">
          Available Networks
          <span className="text-xs font-normal text-muted-foreground">
            {formatLastUpdate(activeProbeLastUpdate)}
          </span>
        </h4>
          <Button 
            onClick={onTriggerScan} 
            disabled={isUpdating || isScanPending || !canEditDeviceConfig} 
            size="sm" 
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isScanPending ? 'animate-spin' : ''}`} />
            {isScanPending ? 'Scanning...' : 'Scan Networks'}
          </Button>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <Signal className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No networks found. Try running a new scan.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium">
          Available Networks
          <span className="text-xs font-normal text-muted-foreground">
            {formatLastUpdate(activeProbeLastUpdate)}
          </span>
        </h4>
        <div className="flex items-center gap-2">
          {steerMode === 'MANUAL' && (
            <Button 
              onClick={() => {
                console.log('Switch to AUTO button clicked!');
                onShowAutoDialog();
              }} 
              disabled={isUpdating || !canEditDeviceConfig} 
              size="sm" 
              variant="outline"
              className="text-black border-[#DFEF97] hover:bg-[#DFEF97]/90"
              style={{ backgroundColor: '#DFEF97' }}
            >
              Switch to AUTO
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? "text-black border-[#DFEF97]" : ""}
            style={filter === 'all' ? { backgroundColor: '#DFEF97' } : {}}
          >
            All
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setFilter('lte')}
            className={filter === 'lte' ? "text-black border-[#DFEF97]" : ""}
            style={filter === 'lte' ? { backgroundColor: '#DFEF97' } : {}}
          >
            LTE
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setFilter('3g')}
            className={filter === '3g' ? "text-black border-[#DFEF97]" : ""}
            style={filter === '3g' ? { backgroundColor: '#DFEF97' } : {}}
          >
            3G
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setFilter('2g')}
            className={filter === '2g' ? "text-black border-[#DFEF97]" : ""}
            style={filter === '2g' ? { backgroundColor: '#DFEF97' } : {}}
          >
            2G
          </Button>
        </div>
        <Button 
          onClick={onTriggerScan} 
          disabled={isUpdating || isScanPending || !canEditDeviceConfig} 
          size="sm" 
          variant="outline"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isScanPending ? 'animate-spin' : ''}`} />
          {isScanPending ? 'Scanning...' : 'Scan'}
        </Button>
      </div>

      {isMobile ? (
        <div className="space-y-4">
          {filteredAndSortedNetworks.map((network, index) => (
            <Card 
              key={`${network.p}-${index}`} 
              className={`${network.s === 2 ? 'ring-2 ring-primary' : ''}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-medium">{network.n || network.p || 'Unknown'}</div>
                    <div className="text-sm text-muted-foreground font-mono">{network.p}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRATBadge(network.a)}
                  </div>
                </div>
                
                 <div className="space-y-2 mb-4">
                   <div className="flex justify-between text-sm">
                     <span>Quality:</span>
                     <NetworkQualityBadge network={network} />
                   </div>
                   <div className="flex justify-between text-sm">
                     <span>Status:</span>
                     <span>{getAttachStatus(network.ok)}</span>
                   </div>
                 </div>
                
                 <Button 
                   onClick={() => onSteerToNetwork(network.p || '', network.n || network.p || 'Unknown', network.a || 0)}
                   disabled={isUpdating || !network.p || network.ok !== 1 || !canEditDeviceConfig}
                   size="sm"
                   className={`w-full ${isCurrentlySelected(network) ? "text-black border-[#DFEF97]" : ""}`}
                   variant={isCurrentlySelected(network) ? "outline" : "outline"}
                   style={isCurrentlySelected(network) ? { backgroundColor: '#DFEF97' } : {}}
                 >
                   {isCurrentlySelected(network) ? 'Selected' : 'Steer to this network'}
                 </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Operator</TableHead>
              <TableHead>PLMN</TableHead>
              <TableHead>RAT</TableHead>
              <TableHead>Quality</TableHead>
              <TableHead>RSRP</TableHead>
              <TableHead>RSRQ</TableHead>
              <TableHead>SNR</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedNetworks.map((network, index) => (
              <TableRow 
                key={`${network.p}-${index}`}
                className={`${network.s === 2 ? 'bg-primary/5' : ''}`}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    {network.n || network.p || 'Unknown'}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm">{network.p || '—'}</TableCell>
                <TableCell>{getRATBadge(network.a)}</TableCell>
                <TableCell><NetworkQualityBadge network={network} /></TableCell>
                <TableCell className="font-mono text-sm">
                  {network.rsrp ? `${network.rsrp} dBm` : '—'}
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {network.rsrq ? `${network.rsrq} dB` : '—'}
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {network.snr ? `${network.snr} dB` : '—'}
                </TableCell>
                <TableCell>{getAttachStatus(network.ok)}</TableCell>
                <TableCell>
                   <Button 
                     onClick={() => onSteerToNetwork(network.p || '', network.n || network.p || 'Unknown', network.a || 0)}
                     disabled={isUpdating || !network.p || network.ok !== 1 || !canEditDeviceConfig}
                     size="sm"
                     className={isCurrentlySelected(network) ? "text-black border-[#DFEF97]" : ""}
                     variant={isCurrentlySelected(network) ? "outline" : "outline"}
                     style={isCurrentlySelected(network) ? { backgroundColor: '#DFEF97' } : {}}
                   >
                     {isCurrentlySelected(network) ? 'Selected' : 'Steer'}
                   </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}